/**
 * *May this caller administer the firm?* — asked once, answered in one place.
 *
 * **Why it is a shared object rather than four lines in each screen.** B2 derived it inline
 * (`KLQ-20260819-006` decision 2); B3 needs the identical answer for `/settings` and for the
 * rail item that points at it, and the chrome needs it on every workspace screen. That is the
 * second appearance, which is where CLAUDE.md retro rule 8 says the filter becomes one object
 * the later callers compose instead of re-deriving.
 *
 * **There is no `GET /workspace/surfaces`, and this does not invent one.** The caller's own
 * row in the member list already names their workspace role, and every action these screens
 * offer — `invite_member`, `change_member_role`, `remove_member`,
 * `change_workspace_settings` — sits at the same floor, `MANAGER`. So one boolean covers all
 * of them, and a second endpoint would be a second source of truth for a fact the client is
 * already holding.
 *
 * **It decides what to draw and never what is permitted.** The server re-resolves authority
 * on every request; a race — somebody demoted mid-session — comes back as a `403` that each
 * screen renders as a sentence. Hiding a control whose only outcome is `403` is a kindness,
 * not a guard.
 *
 * COST. None beyond `GET /auth/me`, which every screen already has. **Until 2026-09-25 this
 * read `GET /workspace/members` — the whole roster — on every claimed screen** to find the
 * caller's own row in it (`docs/2026-09-25-functional-performance-review.md` §3.18): a studio
 * of two hundred people fetched two hundred rows per tab to learn one word. `/auth/me` had
 * carried that word all along — `workspaces[].role`, the role the switcher shows — so the
 * answer now comes off the session payload for the workspace this tab is acting as
 * (`useActiveWorkspace`), and the roster is fetched only by the screens that render it.
 */

import { useMemo } from 'react';

import { useActiveWorkspace } from '@/features/session';

import type { WorkspaceMember, WorkspaceRole } from './api';
import { WORKSPACE_ROLES } from './api';

/**
 * The two roles that carry authority over the firm.
 *
 * TWIN: `ACTION_MIN_LEVEL` in `backend/src/kalqa/core/authz.py`, whose floor for every one of
 * these actions is `MANAGER`, and `WORKSPACE_LEVELS` in `core/levels.py`, where `member` maps
 * to `NONE`. A floor lowered there is a role to add here.
 */
const ADMINISTERS: ReadonlySet<WorkspaceRole> = new Set<WorkspaceRole>(['admin', 'manager']);

export interface StudioAuthority {
  /** `false` while the session is still in flight — see `allowed`. */
  readonly known: boolean;
  /**
   * Whether this caller may staff the firm and change its settings.
   *
   * `false` until the session answers, deliberately: a control that appears and then vanishes
   * for somebody who may not use it is worse than one that appears a moment late.
   */
  readonly allowed: boolean;
  readonly role: WorkspaceRole | null;
}

/**
 * The caller's role in the workspace this tab acts as, off `GET /auth/me`.
 *
 * No `enabled` flag any more: there is no request left for it to gate. `known` is `false` only
 * until the session lands, which `RequireSession` already waits for, so in practice every
 * screen below it gets a decided answer on first render.
 */
export function useStudioAuthority(): StudioAuthority {
  const workspace = useActiveWorkspace();
  const role: WorkspaceRole | null = workspace?.role ?? null;

  return useMemo(() => {
    if (workspace === null) return { known: false, allowed: false, role: null };
    return { known: true, allowed: role !== null && ADMINISTERS.has(role), role };
  }, [workspace, role]);
}

// --- The admin door, per row (`KLQ-20260821-002` §E) --------------------------
//
// The boolean above answers a question about the caller. The two acts below are the first
// controls on this screen whose availability is a question about the *row* as well, so they
// get their own derivation rather than a fourth `canWrite &&` at the call site.
//
// **The point of this file existing is that the two are told apart.** A control missing
// because of who the caller is, is missing from every row — a member sees no role select
// anywhere, and the uniformity is itself the explanation. A control missing because of *which
// colleague this is* looks, on a screen where the row above it has one, exactly like a bug.
// So a row-shaped withholding names its reason and a caller-shaped one stays silent, which is
// what `withheld` carries.

/**
 * Two roles compared by their place in the ladder, strongest first.
 *
 * TWIN: `ROLE_LEVELS` in `backend/src/kalqa/core/levels.py`, via `WORKSPACE_ROLES`, which is
 * ordered by authority for exactly this reason. Deliberately an index and not a copy of the
 * level integers: those are the backend's to renumber (`levels.py` reserves nine free
 * integers between any two roles), and mirroring them here would put a number in this app
 * whose only purpose is to be compared against another number in this app.
 */
function rank(role: WorkspaceRole): number {
  return WORKSPACE_ROLES.indexOf(role);
}

/** Why a credential control is not being offered on this row, when the reason is the row. */
export type CredentialWithholding =
  /** The account calls another firm home — §E3, and the server answers `404`. */
  | 'external'
  /** Equal or greater authority — §E2, and the server answers `403`. */
  | 'peer';

export interface CredentialActs {
  /** *Send a password reset* — `MANAGER` and strictly above this row. */
  readonly mayReset: boolean;
  /** *Set a password* — `ADMIN` and strictly above this row. */
  readonly maySet: boolean;
  /** The sentence the row owes the caller, or `null` when it owes none. */
  readonly withheld: CredentialWithholding | null;
}

const OFFERS_NOTHING: CredentialActs = { mayReset: false, maySet: false, withheld: null };

/**
 * What the admin door offers on one member row — *rendered from the server's answer, not
 * attempted and apologised for* (`KLQ-20260819-004` decision 2).
 *
 * The four bounds of §E, in the order the service applies them, so a control drawn here is a
 * control `workspaces/service.py` would accept:
 *
 * 1. **Never your own row.** The profile screen is the door for your own password, and
 *    `_guard_credential_act` refuses the caller first so the sentence can say so. Nothing is
 *    withheld *from* the row here — the caller has a screen that does this properly — so it
 *    carries no note.
 * 2. **The floor.** `reset_member_password` sits at `MANAGER` and `set_member_password` at
 *    `ADMIN` — the first action in the matrix to use it. TWIN: `ACTION_MIN_LEVEL` in
 *    `backend/src/kalqa/core/authz.py`.
 * 3. **The home workspace** (§E3). A `user_account` is global and a workspace admin is local:
 *    an external collaborator holds a membership row here while their account and their other
 *    firms' authority live elsewhere. `is_home_account` exists on `MemberResponse` for this
 *    one decision.
 * 4. **Strictly above** (§E2), and `may_act_on`'s peer clause is deliberately not the rule:
 *    admins may re-role each other because a firm has to be able to fix its own admin list,
 *    and a credential is not a role. The server spells the comparison out in
 *    `_guard_credential_act`; this mirrors that and not the other one.
 *
 * The server re-decides all four on every request. This decides what to *draw* — a race still
 * comes back as a sentence through `problems.ts`.
 */
export function credentialActs(
  member: WorkspaceMember,
  actorRole: WorkspaceRole | null,
  actorUserId: string | undefined,
): CredentialActs {
  if (actorRole === null || actorUserId === undefined) return OFFERS_NOTHING;
  if (member.user_id === actorUserId) return OFFERS_NOTHING;
  if (!ADMINISTERS.has(actorRole)) return OFFERS_NOTHING;

  // Ordered as the service orders them: the loader runs before the guard, so an account
  // belonging to another firm is out of reach before its role is ever compared.
  if (!member.is_home_account) return { mayReset: false, maySet: false, withheld: 'external' };
  if (rank(actorRole) >= rank(member.role)) {
    return { mayReset: false, maySet: false, withheld: 'peer' };
  }

  // A manager over a member reaches the first and not the second, and that absence is
  // caller-shaped: it is the same on every row they can see, so it earns no sentence.
  return { mayReset: true, maySet: actorRole === 'admin', withheld: null };
}
